import React, { FC, useState } from "react";
import styled from 'styled-components';

import Cell from './Cell';
import { iGrid, iCell, GameState, Player } from '../models/GameModels';

export type GridProps = {
  gridSize: number;
};

const Button = styled.button`
  background: #00CCB4;
  color: white;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  border: 1px solid #03ae9a;
  &:hover {
    background: #03ae9a;
  }
`;

/* Board Grid Component */
const Grid: FC<GridProps> = (props: GridProps) => {

  const { gridSize } = props;

  const GridWrapper = styled.div`
    display: grid;
    grid-template: repeat(${gridSize}, 1fr) / repeat(${gridSize}, 1fr);
    border: 1px solid #00CCB4;
    border-radius: 4px;
  `;

  /* Returns an empty Grid array */
  const generateCellData = (rows: number = 3, cols: number = 3) =>
    [...Array(rows)].map(x => [...Array(cols)].map(x => iCell.null));

  /* Define game state */
  const [gameState, setGameState] = useState<GameState>(GameState.Playing);
  const [currentPlayer, setCurrentPlayer] = useState<Player>(1);
  const [gameData, setGameData] = useState<iGrid>(generateCellData(gridSize, gridSize));

  const togglePlayer = () => {
    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
  }

  const checkIfDraw = () =>
    gameData.every((row) => row.every((cell) => cell > 0));

  const restartGame = () => {
    setGameState(GameState.Playing);
    setGameData(generateCellData(gridSize, gridSize));
    setCurrentPlayer(1);
  }

  const checkIfGameWon = (player: Player) => {
    const checkRow = (player: Player) => {
      let counter = 0;
      gameData.forEach((row) => {
        if (counter < gridSize) {
          row.forEach((cell) => counter = (cell as number === player as number) ? counter += 1 : 0);
        }
      });
      return counter >= gridSize;
    }
    const checkCol = (player: Player) => {
      let counter = 0;
      for (let i = 0; i < gridSize; i++) {
        if (counter < gridSize) {
          gameData.forEach((row) => {
            counter = (row[i] as number === player as number) ? counter += 1 : 0;
          });
        }
      }
      return counter >= gridSize;
    }
    return (checkRow(player) || checkCol(player));
  }


  const cellClicked = (x: number, y: number) => {
    if (gameData[x][y] !== iCell.null) return;
    let gameDataCopy = [...gameData];
    gameDataCopy[x][y] = currentPlayer === Player.Player1 ? iCell.X : iCell.O;
    setGameData(gameDataCopy);
    if (checkIfGameWon(currentPlayer)) {
      setGameState(GameState.Won);
    } else if (checkIfDraw()) {
      setGameState(GameState.Draw);
    } else {
      togglePlayer()
    }
  }

  return (
    <>
      <GridWrapper>
        {gameData.map((rows, rowIndex) => (
          rows.map((cell: iCell, colIndex: number) => (
            <Cell
              key={`${rowIndex}-${colIndex}`}
              cellValue={cell}
              clickFunction={cellClicked}
              coordinates={{ x: rowIndex, y: colIndex }}
              gameOver={gameState !== GameState.Playing}
            />
          ))
        ))}
      </GridWrapper>
      <p>
        {gameState === GameState.Playing && `Player ${currentPlayer}'s Turn`}
        {gameState === GameState.Won && `Player ${currentPlayer} Won the Game!`}
        {gameState === GameState.Draw && `It's a Draw!`}
      </p>
      <Button onClick={restartGame}>Restart Game</Button>
    </>
  );
};

export default Grid;
